import type { LhcMutationGroupSummary } from '@diamond/shared/types';
import { parseAmountToNumber } from '@diamond/sol-admin/utils';

import type { LHCDetailInvoiceRowData } from '../types';

export function resolvePoReferenceAmount(
  rowData: LHCDetailInvoiceRowData | null,
  groupSummary?: LhcMutationGroupSummary | null
): number | undefined {
  if (groupSummary && Number.isFinite(groupSummary.total_invoice_amount)) {
    return groupSummary.total_invoice_amount;
  }

  if (!rowData) return undefined;

  const groupAmount = parseAmountToNumber(rowData.groupTotalInvoiceAmount ?? '');
  if (typeof groupAmount === 'number') {
    return groupAmount;
  }

  return parseAmountToNumber(rowData.invoiceValue ?? '');
}

export function resolvePoOutstandingAmount(
  rowData: LHCDetailInvoiceRowData | null,
  groupSummary?: LhcMutationGroupSummary | null
): number | undefined {
  if (groupSummary && Number.isFinite(groupSummary.outstanding_amount)) {
    return Math.max(0, groupSummary.outstanding_amount);
  }

  const referenceAmount = resolvePoReferenceAmount(rowData, groupSummary);
  if (typeof referenceAmount !== 'number') {
    return undefined;
  }

  const differenceAmount = parseAmountToNumber(rowData?.differenceAmount ?? '');
  if (typeof differenceAmount === 'number') {
    return Math.max(0, differenceAmount);
  }

  return referenceAmount;
}

export function resolveMutationAmountApplied({
  mode,
  rowData,
  groupSummary,
  remainingAmount,
}: {
  mode: 'append' | 'replace';
  rowData: LHCDetailInvoiceRowData | null;
  groupSummary?: LhcMutationGroupSummary | null;
  remainingAmount: number;
}): number | undefined {
  if (!Number.isFinite(remainingAmount) || remainingAmount <= 0) {
    return undefined;
  }

  const targetAmount =
    mode === 'replace'
      ? resolvePoReferenceAmount(rowData, groupSummary)
      : resolvePoOutstandingAmount(rowData, groupSummary);

  if (typeof targetAmount !== 'number' || targetAmount <= 0) {
    return undefined;
  }

  const amountApplied = Math.min(targetAmount, remainingAmount);
  return amountApplied > 0 ? amountApplied : undefined;
}
